import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import {
  HowToReg as RegisterIcon,
  EventAvailable as EventIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import unifiedApi from '../../utils/unifiedApi';
import { useAuth } from '../../contexts/AuthContext';
import EmergencyContactFields from '../../components/formItems/EmergencyContactFields';
import { EMERGENCY_CONTACT_PHONE_ERROR, isValidEmergencyContactPhone } from '../../utils/emergencyContact';

const MIN_DOB = '1900-01-01';
const MIN_AGE_YEARS = 16;

const getMaxDobForMinAge = (minAgeYears) => {
  const now = new Date();
  const year = now.getFullYear() - minAgeYears;
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;

const EventRegistrationPage = () => {
  const { eventSlug } = useParams();
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const maxDob = useRef(getMaxDobForMinAge(MIN_AGE_YEARS));
  const [config, setConfig] = useState(null);
  const sanitizedDescription = useMemo(
    () => config?.description ? DOMPurify.sanitize(config.description) : '',
    [config?.description]
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  const [profileFirstName, setProfileFirstName] = useState('');
  const [differentEmailDialogOpen, setDifferentEmailDialogOpen] = useState(false);

  const [form, setForm] = useState({
    email: '',
    confirmEmail: '',
    firstName: '',
    lastName: '',
    birthday: '',
    phone: '',
    postalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    waiverAccepted: false,
    customFieldValue: ''
  });

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await unifiedApi.getPublicEventRegistrationConfig(eventSlug);
        if (!data) {
          setError('Registration is not available for this event.');
          return;
        }
        setConfig(data);
      } catch (err) {
        setError('Failed to load event registration information.');
        console.error('Error loading event registration config:', err);
      } finally {
        setLoading(false);
      }
    };

    if (eventSlug) {
      loadConfig();
    }
  }, [eventSlug]);

  // Prefill form from authenticated user's profile
  useEffect(() => {
    const prefillFromProfile = async () => {
      if (!isAuthenticated || authLoading) return;
      try {
        const profile = await unifiedApi.getMe();
        if (!profile) return;

        setProfileFirstName(profile.firstName || '');

        const formatBirthDate = (dateStr) => {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return '';
          const year = d.getUTCFullYear();
          const month = String(d.getUTCMonth() + 1).padStart(2, '0');
          const day = String(d.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setForm(prev => ({
          ...prev,
          email: prev.email === '' ? (profile.email || '') : prev.email,
          confirmEmail: prev.confirmEmail === '' ? (profile.email || '') : prev.confirmEmail,
          firstName: prev.firstName === '' ? (profile.firstName || '') : prev.firstName,
          lastName: prev.lastName === '' ? (profile.lastName || '') : prev.lastName,
          birthday: prev.birthday === '' ? (formatBirthDate(profile.birthDate) || '') : prev.birthday,
          phone: prev.phone === '' ? (profile.phoneNumber || '') : prev.phone,
          postalCode: prev.postalCode === '' ? (profile.postalCode || '') : prev.postalCode,
          emergencyContactName: prev.emergencyContactName === '' ? (profile.emergencyContactName || '') : prev.emergencyContactName,
          emergencyContactPhone: prev.emergencyContactPhone === '' ? (profile.emergencyContactPhone || '') : prev.emergencyContactPhone
        }));
        setPrefilled(true);
      } catch (err) {
        console.error('Error loading profile for prefill:', err);
      }
    };

    prefillFromProfile();
  }, [isAuthenticated, authLoading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBirthdayChange = (e) => {
    const value = e.target.value;
    if (value && !/^\d{0,4}(-\d{0,2}(-\d{0,2})?)?$/.test(value)) {
      return;
    }
    setForm(prev => ({ ...prev, birthday: value }));
  };

  const isValidCalendarDate = (dateString) => {
    if (!dateString) return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    const [year, month, day] = dateString.split('-').map(Number);
    if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return false;
    return true;
  };

  // Returns a user-facing error message for the birthday field, or null if valid.
  const getBirthdayError = (dateString) => {
    if (!dateString) return null;
    if (!isValidCalendarDate(dateString)) return 'Please enter a valid date (YYYY-MM-DD).';
    if (dateString < MIN_DOB) return 'Please enter a valid date of birth.';
    if (dateString > maxDob.current) return `You must be at least ${MIN_AGE_YEARS} years old to register.`;
    return null;
  };

  const birthdayError = getBirthdayError(form.birthday);
  const isValidBirthday = form.birthday !== '' && birthdayError === null;
  const emailMismatch = form.confirmEmail !== '' && form.email.toLowerCase() !== form.confirmEmail.toLowerCase();
  const birthdayInvalid = form.birthday !== '' && birthdayError !== null;
  const isSoldOut = config?.registrationOpen && config?.availableSlots === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailMismatch) {
      setSubmitError('Email addresses do not match.');
      return;
    }
    if (!isValidBirthday) {
      setSubmitError(birthdayError || 'Please enter a valid date of birth.');
      return;
    }
    if (!form.emergencyContactName.trim()) {
      setSubmitError('Emergency contact name is required.');
      return;
    }
    if (!isValidEmergencyContactPhone(form.emergencyContactPhone)) {
      setSubmitError(EMERGENCY_CONTACT_PHONE_ERROR);
      return;
    }
    await submitRegistration();
  };

  const submitRegistration = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const submitData = {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        birthday: form.birthday ? new Date(form.birthday).toISOString() : undefined,
        phone: form.phone,
        postalCode: form.postalCode,
        emergencyContactName: form.emergencyContactName.trim(),
        emergencyContactPhone: form.emergencyContactPhone.trim(),
        waiverAccepted: form.waiverAccepted,
        customFieldValue: config?.customFieldEnabled ? form.customFieldValue : undefined
      };

      const result = await unifiedApi.submitEventRegistration(eventSlug, submitData);

      if (result?.sessionToken) {
        localStorage.setItem('eventRegistrationSessionToken', result.sessionToken);
      }

      if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      const message = err?.message || 'Failed to submit registration.';
      setSubmitError(message);
      console.error('Event registration submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4} px={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!config?.registrationOpen) {
    return (
      <Box mt={4} px={2}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RegisterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Registration Closed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registration for {config?.name || 'this event'} is not currently open.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isSoldOut) {
    return (
      <Box mt={4} px={2}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RegisterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Sold Out
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All spots for {config.name} have been filled.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <EventIcon color="primary" />
          Register for {config.name}
        </Typography>
      </Box>

      {/* Event Description */}
      {sanitizedDescription && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              '& p': { mt: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        </Paper>
      )}

      {/* Event Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Event Details</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Price: ${formatCurrency(config.priceInCents)}`}
            color="primary"
            variant="filled"
          />
          <Chip
            label={`${config.availableSlots} of ${config.totalSlots} spots available`}
            color={config.availableSlots <= 5 ? 'warning' : 'default'}
            variant="outlined"
          />
        </Stack>
      </Paper>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>
      )}

      {prefilled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome back, {profileFirstName}! We&apos;ve filled in your details from your profile.{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => setDifferentEmailDialogOpen(true)}
            sx={{ verticalAlign: 'baseline' }}
          >
            Want to use a different email?
          </Link>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Personal Information</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              name="firstName"
              label="First Name"
              value={form.firstName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="lastName"
              label="Last Name"
              value={form.lastName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              slotProps={prefilled ? { htmlInput: { readOnly: true } } : {}}
              sx={prefilled ? { '& .MuiInputBase-input': { color: 'text.secondary' } } : {}}
            />
            <TextField
              name="confirmEmail"
              label="Confirm Email"
              type="email"
              value={form.confirmEmail}
              onChange={handleChange}
              required
              fullWidth
              slotProps={prefilled ? { htmlInput: { readOnly: true } } : {}}
              sx={prefilled ? { '& .MuiInputBase-input': { color: 'text.secondary' } } : {}}
              error={emailMismatch}
              helperText={emailMismatch ? 'Email addresses do not match' : ''}
            />
            <TextField
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="birthday"
              label="Date of Birth"
              type="date"
              value={form.birthday}
              onChange={handleBirthdayChange}
              required
              fullWidth
              error={birthdayInvalid}
              helperText={birthdayInvalid ? birthdayError : ''}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: MIN_DOB, max: maxDob.current }
              }}
            />
            <TextField
              name="postalCode"
              label="Postal Code"
              value={form.postalCode}
              onChange={handleChange}
              required
              fullWidth
            />
          </Box>
        </Paper>

        <EmergencyContactFields
          name={form.emergencyContactName}
          phone={form.emergencyContactPhone}
          onChange={handleChange}
        />

        {/* Custom Field */}
        {config.customFieldEnabled && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {config.customFieldLabel || 'Additional Information'}
            </Typography>
            <TextField
              name="customFieldValue"
              value={form.customFieldValue}
              onChange={handleChange}
              multiline
              minRows={3}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 2000, 'aria-label': config.customFieldLabel || 'Additional information' } }}
            />
          </Paper>
        )}

        {/* Waiver */}
        {config.waiverText && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Waiver</Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {config.waiverText}
              </Typography>
            </Paper>
            <FormControlLabel
              control={
                <Checkbox
                  name="waiverAccepted"
                  checked={form.waiverAccepted}
                  onChange={handleChange}
                />
              }
              label="I have read and accept the waiver"
            />
          </Paper>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitting || (config.waiverText && !form.waiverAccepted) || emailMismatch || birthdayInvalid}
          fullWidth
          sx={{ py: 1.5 }}
        >
          {submitting ? <CircularProgress size={24} /> : 'Proceed to Payment'}
        </Button>
      </form>

      <Dialog
        open={differentEmailDialogOpen}
        onClose={() => setDifferentEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Use a Different Email?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            To register with a different email address, you&apos;ll need to log out first, then refresh the page (Ctrl+R) before starting a new registration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDifferentEmailDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => logout()}>
            Log Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventRegistrationPage;
